import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getGuideCategoryToBoats, getGuideBoatsListing } from "store/boats"
import { getRoles } from "store/roles"
import { getStaffMembers, selectStaffMembers } from "store/staff"
import { transformBoatsToEquipment } from "../utils/resourceHelpers"

/**
 * Hook to fetch and transform resource data (boats, categories, roles, staff)
 * @returns {Object} Resource data and options
 */
export const useResourceData = () => {
  const dispatch = useDispatch()

  const { items: categoryItems } = useSelector((state) => state.boats.category_to_boats)
  const { items: boats } = useSelector((state) => state.boats)
  const { items: roles } = useSelector((state) => state.roles)
  const staffMembers = useSelector(selectStaffMembers) || []

  // Fetch data on mount
  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
    dispatch(getRoles())
    dispatch(getGuideBoatsListing())
    dispatch(getStaffMembers())
  }, [dispatch])

  // Transform boats to equipment format
  const equipmentData = useMemo(() => transformBoatsToEquipment(boats), [boats])

  // Transform staff members to crew member format
  const crewMemberData = useMemo(
    () =>
      staffMembers.map((staff) => ({
        ...staff,
        id: staff.id,
        name: staff.name || `${staff.firstName || staff.first_name || ""} ${staff.lastName || staff.last_name || ""}`.trim(),
        // Preserve role object structure (Redux transforms role to string, but table needs object)
        role: typeof staff.role === 'string'
          ? { id: staff.roleId, name: staff.role, role_type: staff.roleType }
          : staff.role
      })),
    [staffMembers]
  )

  // Category options for multi-select
  const categoryOptions = useMemo(
    () => [
      { label: "All", value: "all" },
      ...categoryItems.map((category) => ({
        label: category.name,
        value: String(category.id)
      }))
    ],
    [categoryItems]
  )

  // Role options for multi-select
  const roleOptions = useMemo(
    () => [
      { label: "All", value: "all" },
      ...roles.map((role) => ({
        label: role.name,
        value: String(role.id)
      }))
    ],
    [roles]
  )

  return {
    equipmentData,
    crewMemberData,
    categoryOptions,
    roleOptions
  }
}
